<template>
  <div v-if="productoProp" class="yt-dashboard-theme pa-2 pa-sm-6">

    <!-- Selector tipo_stock: no aplica con una sola presentación -->
    <v-card v-if="!simplificado" flat color="#161616" class="rounded-xl pa-4 pa-sm-6 border-yt mb-4 mb-sm-6">
      <div class="d-flex flex-wrap align-center mb-4" style="gap: 8px">
        <div class="section-title">
          <v-icon small color="#3ea6ff" class="mr-2">mdi-archive-cog-outline</v-icon>
          Control de stock
        </div>
        <v-spacer />
        <v-chip
          v-if="tieneMovimientos"
          small
          label
          color="rgba(255,152,0,0.12)"
          text-color="#ffb300"
          class="border-warning font-weight-bold py-1"
          style="height: auto; white-space: normal"
        >
          <v-icon left x-small>mdi-lock-outline</v-icon>
          No modificable — existen movimientos de stock
        </v-chip>
      </div>

      <v-item-group
        v-if="tipoStockOpciones.length"
        :value="tipoStockLocal"
        mandatory
        @change="seleccionarTipoStock"
      >
        <v-row dense>
          <v-col
            v-for="opcion in tipoStockOpciones"
            :key="opcion.value"
            cols="12"
            md="4"
          >
            <v-item v-slot="{ active, toggle }" :value="opcion.value">
              <v-card
                flat
                :color="active ? 'rgba(62,166,255,0.08)' : '#1a1a1a'"
                :class="['tipo-stock-card', { 'tipo-stock-active': active, 'tipo-stock-disabled': tieneMovimientos }]"
                @click="!tieneMovimientos && toggle()"
              >
                <div class="d-flex align-start pa-4">
                  <v-icon
                    :color="active ? '#3ea6ff' : 'grey darken-1'"
                    class="mr-3 mt-1"
                    small
                  >{{ opcion.icon }}</v-icon>
                  <div>
                    <div class="tipo-stock-label" :class="active ? 'blue-label' : ''">
                      {{ opcion.label }}
                    </div>
                    <div class="tipo-stock-desc mt-1">{{ opcion.desc }}</div>
                  </div>
                  <v-spacer />
                  <v-icon v-if="active" color="#3ea6ff" small>mdi-check-circle</v-icon>
                </div>
              </v-card>
            </v-item>
          </v-col>
        </v-row>
      </v-item-group>

      <div v-if="tipoStockModificado" class="d-flex justify-end mt-4">
        <v-btn
          text
          small
          color="grey"
          class="text-none mr-2"
          :disabled="loadingTipoStock"
          @click="descartarTipoStock"
        >
          Descartar
        </v-btn>
        <v-btn
          depressed
          small
          color="#3ea6ff"
          class="text-none font-weight-bold black--text"
          :loading="loadingTipoStock"
          @click="guardarTipoStock"
        >
          Guardar
        </v-btn>
      </div>
    </v-card>

    <!-- Lista de presentaciones -->
    <v-card flat color="#161616" class="rounded-xl pa-4 pa-sm-6 border-yt">
      <div class="d-flex align-center mb-4">
        <div class="section-title">
          <v-icon small color="#3ea6ff" class="mr-2">mdi-package-variant</v-icon>
          Presentaciones
        </div>
        <v-spacer />
        <v-btn
          v-if="simplificado"
          text
          x-small
          color="#3ea6ff"
          class="text-none"
          @click="modoAvanzado = true"
        >
          Modo avanzado
        </v-btn>
      </div>
      <div class="caption grey--text mb-4">
        Define los formatos en los que este producto se usa en ventas o compras.
      </div>
      <ProductoPresentacionesTable
        :productoId="productoProp.id"
        :value="productoProp.presentaciones"
        :tipoStock="productoProp.tipo_stock"
        :modoAvanzado="modoAvanzado"
        @updated="$emit('refresh-producto')"
      />
    </v-card>

  </div>
</template>

<script>
import axios from "axios";
import ProductoPresentacionesTable from "@/views/empresa/productos/components/ProductoPresentacionesTable.vue";

export default {
  name: "ProductoPresentacionesView",
  components: { ProductoPresentacionesTable },
  props: {
    productoProp: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      tipoStockOpciones: [],
      tipoStockLocal: null,
      loadingTipoStock: false,
      // No persiste: arranca simple en cada visita, salvo que ya haya
      // más de una presentación (ahí no tiene sentido "simplificar").
      modoAvanzado: false,
    };
  },

  created() {
    this.fetchOpciones();
  },

  computed: {
    simplificado() {
      const cantidad = (this.productoProp && this.productoProp.presentaciones || []).length;
      return !this.modoAvanzado && cantidad <= 1;
    },
    tieneMovimientos() {
      return !!(this.productoProp && this.productoProp.tiene_movimientos_stock);
    },
    tipoStockModificado() {
      return this.productoProp && this.tipoStockLocal !== null && this.tipoStockLocal !== this.productoProp.tipo_stock;
    },
  },

  watch: {
    'productoProp.tipo_stock': {
      immediate: true,
      handler(val) {
        this.tipoStockLocal = val;
      },
    },
  },

  methods: {
    async fetchOpciones() {
      const iconos = {
        IND: "mdi-cube-outline",
        BASE: "mdi-vector-link",
        TRANSF: "mdi-swap-horizontal",
      };
      const descs = {
        IND: "Cada presentación maneja su propio stock de forma independiente.",
        BASE: "El stock de las derivadas se calcula proporcionalmente desde la unidad base.",
        TRANSF: "El stock fluye entre presentaciones mediante transferencias.",
      };
      try {
        const { data } = await axios.get("/productos/productos/opciones");
        this.tipoStockOpciones = (data.tipo_stock || []).map((op) => ({
          ...op,
          icon: iconos[op.value] || "mdi-help-circle-outline",
          desc: descs[op.value] || "",
        }));
      } catch {
        // fallback vacío — el selector no se muestra si falla
      }
    },

    seleccionarTipoStock(nuevoTipo) {
      if (!this.tieneMovimientos) this.tipoStockLocal = nuevoTipo;
    },

    descartarTipoStock() {
      this.tipoStockLocal = this.productoProp.tipo_stock;
    },

    async guardarTipoStock() {
      this.loadingTipoStock = true;
      try {
        await axios.patch(`/productos/productos/${this.productoProp.id}`, {
          tipo_stock: this.tipoStockLocal,
        });
        this.$emit("refresh-producto");
      } catch (e) {
        const errors = e?.response?.data;
        const msg = errors?.tipo_stock?.[0] || "Error al actualizar el tipo de stock";
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          color: "error",
          text: msg,
          timeout: 6000,
        });
        this.tipoStockLocal = this.productoProp.tipo_stock;
      } finally {
        this.loadingTipoStock = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.yt-dashboard-theme {
  background-color: #0f0f0f;
  min-height: 100vh;
}
.border-yt {
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.border-warning {
  border: 1px solid rgba(255, 179, 0, 0.3) !important;
}
.section-title {
  color: #fff;
  font-size: 0.75rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  display: flex;
  align-items: center;
}

.tipo-stock-card {
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 10px !important;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  &:hover:not(.tipo-stock-disabled) {
    border-color: rgba(62, 166, 255, 0.3) !important;
  }
  &.tipo-stock-active {
    border-color: rgba(62, 166, 255, 0.5) !important;
  }
  &.tipo-stock-disabled {
    cursor: default;
    opacity: 0.6;
  }
}

.tipo-stock-label {
  font-size: 0.8rem;
  font-weight: 800;
  color: #ccc;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  &.blue-label { color: #3ea6ff; }
}

.tipo-stock-desc {
  font-size: 0.72rem;
  color: #666;
  line-height: 1.4;
}
</style>
